"use client";

import { useEffect } from "react";

/**
 * Last-resort error boundary. Catches errors thrown in the ROOT layout
 * itself — the one case `error.tsx` cannot catch. It replaces the root
 * layout, so it must render its own `<html>` and `<body>`, and it cannot
 * assume globals.css loaded (the failure may be upstream of it). Styling is
 * therefore inline and self-contained.
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    console.error("[global-error-boundary]", error);
  }, [error]);

  return (
    <html lang="en">
      <body
        style={{
          margin: 0,
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "1.25rem",
          padding: "1.5rem",
          textAlign: "center",
          background: "#0b0b0f",
          color: "#e7e7ea",
          fontFamily:
            "system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
        }}
      >
        <div>
          <h1 style={{ fontSize: "1.25rem", fontWeight: 700, margin: "0 0 0.5rem" }}>
            CTRL-A hit an unexpected error
          </h1>
          <p style={{ fontSize: "0.875rem", color: "#a1a1aa", maxWidth: "28rem", margin: 0 }}>
            The app failed to load. This has been logged. Please try reloading.
          </p>
          {error.digest && (
            <p style={{ fontSize: "0.75rem", color: "#71717a", marginTop: "0.5rem" }}>
              Reference: {error.digest}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={reset}
          style={{
            border: "none",
            borderRadius: "0.5rem",
            padding: "0.5rem 1rem",
            fontSize: "0.875rem",
            fontWeight: 600,
            color: "#ffffff",
            background: "#6366f1",
            cursor: "pointer",
          }}
        >
          Reload
        </button>
      </body>
    </html>
  );
}
